import { useState, useRef, useCallback } from 'react'
import { X, Plus, Search } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../config/api'
import { inputClass } from '../styles/forms'
import CreateVoterModal from './CreateVoterModal'

interface VoterOption {
  id: number
  name: string
  phone: string
  nickname?: string | null
}

interface VoterSearchSelectProps {
  label?: string
  value: string
  onChange: (voterId: string, voter?: VoterOption) => void
  placeholder?: string
  /** z-index for dropdown (use high value inside modals) */
  dropdownZIndex?: number
  /** Tipo de cadastro ao criar novo */
  createType?: 'contact' | 'voter'
}

export default function VoterSearchSelect({ label = 'Solicitante', value, onChange, placeholder = 'Buscar por nome, apelido ou CPF...', dropdownZIndex = 100003, createType = 'contact' }: VoterSearchSelectProps) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<VoterOption[]>([])
  const [selectedName, setSelectedName] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchVoters = useCallback(async (q: string, p: number, append = false) => {
    setLoading(true)
    try {
      const params: any = { per_page: 15, page: p }
      if (q.length >= 2) params.search = q
      const res = await axios.get(buildApiUrl('/v1/voters'), { params })
      const data = res.data.data.map((v: any) => ({ id: v.id, name: v.name, phone: v.phone, nickname: v.nickname }))
      if (append) {
        setResults(prev => [...prev, ...data])
      } else {
        setResults(data)
      }
      setTotal(res.data.meta?.total || 0)
      setHasMore(p < (res.data.meta?.last_page || 1))
    } catch { /* */ } finally { setLoading(false) }
  }, [])

  function handleSearch(q: string) {
    setSearch(q)
    setPage(1)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchVoters(q, 1), 300)
  }

  function handleFocus() {
    if (results.length === 0) fetchVoters('', 1)
    setDropdownOpen(true)
  }

  function handleScroll() {
    const el = scrollRef.current
    if (!el || loading || !hasMore) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchVoters(search, nextPage, true)
    }
  }

  function handleSelect(voter: VoterOption) {
    onChange(String(voter.id), voter)
    setSelectedName(voter.name)
    setSearch('')
    setDropdownOpen(false)
  }

  function handleClear() {
    onChange('')
    setSelectedName('')
    setSearch('')
  }

  function handleCreated(voter: { id: number; name: string; phone: string }) {
    onChange(String(voter.id), { ...voter, nickname: null })
    setSelectedName(voter.name)
    setCreateModalOpen(false)
    setDropdownOpen(false)
  }

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}
      <div className="relative">
        <input
          value={value ? selectedName : search}
          onChange={e => {
            if (value) { handleClear() }
            handleSearch(e.target.value)
            setDropdownOpen(true)
          }}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={inputClass}
          readOnly={!!value}
        />
        {value ? (
          <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        ) : (
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
        )}

        {dropdownOpen && !value && (
          <>
            <div className="fixed inset-0" style={{ zIndex: dropdownZIndex - 1 }} onClick={() => setDropdownOpen(false)} />
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="absolute left-0 right-0 top-12 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
              style={{ zIndex: dropdownZIndex }}
            >
              {/* Botão adicionar */}
              <button
                onClick={() => { setDropdownOpen(false); setCreateModalOpen(true) }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-brand-600 hover:bg-brand-50 border-b border-gray-100 font-medium"
              >
                <Plus className="w-4 h-4" /> Cadastrar novo contato
              </button>

              {results.length === 0 && !loading ? (
                <p className="px-4 py-3 text-sm text-gray-400 text-center">
                  {search.length >= 2 ? 'Nenhum contato encontrado' : 'Digite para buscar'}
                </p>
              ) : (
                results.map(v => (
                  <button key={v.id} onClick={() => handleSelect(v)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-gray-900">{v.name}</span>
                        {v.nickname && <span className="text-xs text-gray-400 ml-2">({v.nickname})</span>}
                      </div>
                      {v.phone && <span className="text-xs text-gray-400">{v.phone}</span>}
                    </div>
                  </button>
                ))
              )}

              {loading && (
                <div className="py-2 text-center">
                  <div className="w-5 h-5 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto" />
                </div>
              )}

              {results.length > 0 && (
                <div className="px-4 py-2 text-[10px] text-gray-400 text-center border-t border-gray-100">
                  {results.length} de {total} contatos
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <CreateVoterModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCreated}
        defaultName={search}
        type={createType}
      />
    </div>
  )
}
